"use client";
import { defaultLanguage } from "@/lib/locale/config";
import { languageFromPath, localizeHref } from "@/lib/locale/paths";
/**
 * Nhost v4 React Integration
 *
 * React hooks, providers, and Apollo client for Nhost v4.
 * All exports require "use client" context.
 *
 * Usage:
 * ```tsx
 * import {
 *   useAccessToken,
 *   useAuthenticationStatus,
 *   NhostAuthProvider,
 *   getApolloClient,
 * } from "@/lib/nhost/react";
 * ```
 */

import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { ApolloProvider } from "@apollo/client/react";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient as createWsClient, type Client as WsClient } from "graphql-ws";
import posthog from "posthog-js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { env } from "@/env";
import {
  buildGoogleOAuthRecoveryPath,
  captureSensitiveAuthUrl,
  clearPendingGoogleOAuth,
  consumeCapturedAuthUrl,
  getGoogleOAuthSessionStorage,
  readPendingGoogleOAuth,
  sanitizeAuthUrl,
  toGoogleOAuthAnalyticsEntryPoint,
} from "@/lib/auth/google-oauth";
import { getFreshAccessToken } from "./access-token";
import type { User } from "./client";
import { nhost } from "./client";
import { useHasuraClaims, useNhostSession } from "./session-hooks";

export {
  useAccessToken,
  useAuthenticationStatus,
  useDecodedAccessToken,
  useHasuraClaims,
  useNhostSession,
  useSignOut,
  useUserData,
  useUserId,
} from "./session-hooks";

// ============================================================================
// Types
// ============================================================================

export interface SignInResult {
  isSuccess: boolean;
  isError: boolean;
  error: { message: string; error?: string; status?: number } | null;
  user?: User | null;
  needsEmailVerification?: boolean;
  mfa?: { ticket: string } | null;
}

export interface SignUpResult {
  isSuccess: boolean;
  isError: boolean;
  error: { message: string; error?: string; status?: number } | null;
  needsEmailVerification?: boolean;
}

// ============================================================================
// Sign In/Up/Out Hooks
// ============================================================================

export function useSignInEmailPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SignInResult["error"]>(null);

  const signInEmailPassword = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await nhost.auth.signInEmailPassword({ email, password });
        setIsLoading(false);

        if (result.body?.mfa) {
          return { isSuccess: false, isError: false, error: null, mfa: result.body.mfa };
        }
        if (result.body?.session) {
          return { isSuccess: true, isError: false, error: null, user: result.body.session.user };
        }
        return { isSuccess: false, isError: true, error: { message: "Sign-in failed" } };
      } catch (e: any) {
        setIsLoading(false);
        if (e?.body?.error === "unverified-user") {
          return {
            isSuccess: false,
            isError: true,
            error: { message: e.body?.message || "Email not verified", error: "unverified-user" },
            needsEmailVerification: true,
          };
        }
        const errorResult: SignInResult = {
          isSuccess: false,
          isError: true,
          error: {
            message: e?.body?.message || e?.message || "Sign-in failed",
            error: e?.body?.error,
            status: e?.status,
          },
        };
        setError(errorResult.error);
        return errorResult;
      }
    },
    []
  );

  return { signInEmailPassword, isLoading, error };
}

export function useSignUpEmailPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<SignUpResult["error"]>(null);

  const signUpEmailPassword = useCallback(
    async (
      email: string,
      password: string,
      options?: {
        redirectTo?: string;
        locale?: string;
        metadata?: Record<string, unknown>;
        displayName?: string;
        defaultRole?: string;
        allowedRoles?: string[];
      },
      requestOptions?: { headers?: Record<string, string> }
    ): Promise<SignUpResult> => {
      setIsLoading(true);
      setIsSuccess(false);
      setError(null);

      try {
        const fetchOptions = requestOptions?.headers
          ? { headers: requestOptions.headers }
          : undefined;
        const result = await nhost.auth.signUpEmailPassword(
          {
            email,
            password,
            options: {
              redirectTo: options?.redirectTo,
              locale: options?.locale,
              metadata: options?.metadata,
              displayName: options?.displayName,
              defaultRole: options?.defaultRole,
              allowedRoles: options?.allowedRoles,
            },
          },
          fetchOptions
        );
        setIsLoading(false);

        if (result.body?.session) {
          setIsSuccess(true);
          return { isSuccess: true, isError: false, error: null };
        }
        return { isSuccess: true, isError: false, error: null, needsEmailVerification: true };
      } catch (e: any) {
        setIsLoading(false);
        const errorResult: SignUpResult = {
          isSuccess: false,
          isError: true,
          error: {
            message: e?.body?.message || e?.message || "Sign-up failed",
            error: e?.body?.error,
            status: e?.status,
          },
          needsEmailVerification: e?.body?.error === "unverified-user",
        };
        setError(errorResult.error);
        return errorResult;
      }
    },
    []
  );

  return { signUpEmailPassword, isLoading, isSuccess, error };
}

export function useSignInAnonymous() {
  const session = useNhostSession();
  const user = session?.user ?? null;

  const signInAnonymous = useCallback(async () => {
    try {
      const result = await nhost.auth.signInAnonymous();
      return {
        isSuccess: !!result.body?.session,
        isError: false,
        error: null,
        session: result.body?.session,
      };
    } catch (e: any) {
      return {
        isSuccess: false,
        isError: true,
        error: {
          message: e?.body?.message || e?.message || "Anonymous sign-in failed",
          error: e?.body?.error,
        },
      };
    }
  }, []);

  return { signInAnonymous, user };
}

export function useSendVerificationEmail() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<{ message: string } | null>(null);

  const sendEmail = useCallback(async (email: string, options?: { redirectTo?: string }) => {
    setIsLoading(true);
    setError(null);
    try {
      await nhost.auth.sendVerificationEmail({
        email,
        options: { redirectTo: options?.redirectTo },
      });
      setIsSent(true);
      setIsLoading(false);
    } catch (e: any) {
      setError({ message: e?.data?.message || e?.message || "Failed to send verification email" });
      setIsLoading(false);
    }
  }, []);

  return { sendEmail, isLoading, isSent, error };
}

// ============================================================================
// Admin Check Hook
// ============================================================================

export function useIsAdmin(): { isAdmin: boolean } {
  const claims = useHasuraClaims();
  const roles = claims?.["x-hasura-allowed-roles"];
  const isAdmin = Array.isArray(roles) && roles.includes("admin");
  return { isAdmin };
}

// ============================================================================
// Nhost Client Hook
// ============================================================================

export function useNhost() {
  return nhost;
}

// ============================================================================
// Apollo Client
// ============================================================================

let apolloClient: ApolloClient | null = null;
let wsClient: WsClient | null = null;
let sessionUnsubscribe: (() => void) | null = null;

function createHttpLink(): HttpLink {
  return new HttpLink({
    uri: env.NEXT_PUBLIC_NHOST_GQL_URL,
    fetch: async (uri, options) => {
      const token = await getFreshAccessToken();
      const headers = new Headers(options?.headers);
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return fetch(uri, { ...options, headers });
    },
  });
}

function createWsLink(): GraphQLWsLink {
  const wsUrl = env.NEXT_PUBLIC_NHOST_GQL_URL.replace("https://", "wss://").replace(
    "http://",
    "ws://"
  );

  wsClient = createWsClient({
    url: wsUrl,
    connectionParams: async () => {
      const token = await getFreshAccessToken();
      if (token) {
        return { headers: { authorization: `Bearer ${token}` } };
      }
      return {};
    },
    lazy: true,
    shouldRetry: () => true,
    retryAttempts: Infinity,
    retryWait: async (retryCount) => {
      const delay = Math.min(1000 * 2 ** retryCount, 30000);
      await new Promise((resolve) => setTimeout(resolve, delay));
    },
  });

  return new GraphQLWsLink(wsClient);
}

function createApolloClient(): ApolloClient {
  const httpLink = createHttpLink();
  const wsLink = createWsLink();

  const splitLink = ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === "OperationDefinition" && definition.operation === "subscription";
    },
    wsLink,
    httpLink
  );

  return new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
    defaultOptions: { watchQuery: { fetchPolicy: "cache-and-network" } },
  });
}

function initSessionSync(): () => void {
  if (sessionUnsubscribe) return sessionUnsubscribe;

  sessionUnsubscribe = nhost.sessionStorage.onChange((session) => {
    if (wsClient && session) {
      wsClient.terminate();
    }
  });

  return sessionUnsubscribe;
}

export function getApolloClient(): ApolloClient {
  if (typeof window === "undefined") {
    return createApolloClient();
  }
  if (!apolloClient) {
    apolloClient = createApolloClient();
    initSessionSync();
  }
  return apolloClient;
}

export function resetApolloClient(): void {
  if (sessionUnsubscribe) {
    sessionUnsubscribe();
    sessionUnsubscribe = null;
  }
  if (wsClient) {
    wsClient.terminate();
    wsClient = null;
  }
  if (apolloClient) {
    apolloClient.stop();
    apolloClient.clearStore();
    apolloClient = null;
  }
}

// ============================================================================
// Provider Components
// ============================================================================

interface NhostAuthProviderProps {
  children: React.ReactNode;
  loadingComponent?: React.ReactNode;
  waitForSession?: boolean;
}

export function NhostAuthProvider({
  children,
  loadingComponent,
  waitForSession = false,
}: NhostAuthProviderProps) {
  const [isReady, setIsReady] = useState(!waitForSession);

  useEffect(() => {
    if (!waitForSession) return;

    const initAuth = async () => {
      const session = nhost.getUserSession();
      if (session) {
        try {
          await nhost.refreshSession(60);
        } catch (e) {
          console.error("Failed to refresh session on init:", e);
        }
      }
      setIsReady(true);
    };

    initAuth();
  }, [waitForSession]);

  if (!isReady) {
    return <>{loadingComponent ?? null}</>;
  }

  return <ApolloProvider client={getApolloClient()}>{children}</ApolloProvider>;
}

export function NhostSessionProvider({
  children,
  waitForSession = false,
}: {
  children: React.ReactNode;
  waitForSession?: boolean;
}) {
  const [isReady, setIsReady] = useState(!waitForSession);

  useEffect(() => {
    if (!waitForSession) return;

    const initAuth = async () => {
      const session = nhost.getUserSession();
      if (session) {
        try {
          await nhost.refreshSession(60);
        } catch (e) {
          console.error("Failed to refresh session on init:", e);
        }
      }
      setIsReady(true);
    };

    initAuth();
  }, [waitForSession]);

  if (!isReady) {
    return null;
  }

  return <>{children}</>;
}

// ============================================================================
// URL Parameter Auth Handler
// ============================================================================

/**
 * Hook to handle legacy authentication URL parameters and the Nhost
 * invalid-provider-state fallback that cannot reach the PKCE callback route.
 *
 * In v4 SDK, this needs to be done manually - the old NhostProvider did this automatically.
 *
 * URL parameters handled:
 * - refreshToken: Used to establish a session from legacy and email links
 * - type: The type of auth flow (e.g., "passwordlessEmail")
 */
export function useAuthUrlHandler(): { isProcessing: boolean; establishedSession: boolean } {
  const [isProcessing, setIsProcessing] = useState(true);
  const [establishedSession, setEstablishedSession] = useState(false);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const handleUrlParams = async () => {
      if (typeof window === "undefined") return;

      captureSensitiveAuthUrl();
      const capturedUrl = consumeCapturedAuthUrl();
      const currentUrl = new URL(window.location.href);
      const params = currentUrl.searchParams;

      if (
        capturedUrl?.kind === "invalid-provider-state" ||
        params.get("error") === "invalid-state"
      ) {
        const storage = getGoogleOAuthSessionStorage();
        const pendingResult = readPendingGoogleOAuth(storage);
        window.history.replaceState({}, "", sanitizeAuthUrl(currentUrl.href));

        const pending = pendingResult.status === "valid" ? pendingResult.pending : null;
        clearPendingGoogleOAuth(storage);
        const entryPoint = pending?.entryPoint ?? "sign-in";
        posthog.capture("auth_oauth_failed", {
          entry_point: toGoogleOAuthAnalyticsEntryPoint(entryPoint),
          reason: "provider_state_expired",
        });
        window.location.replace(
          localizeHref(
            buildGoogleOAuthRecoveryPath({
              destination: pending?.destination ?? "/app/dashboard/",
              entryPoint,
              reason: "provider_state_expired",
            }),
            languageFromPath(currentUrl.pathname) ?? defaultLanguage
          )
        );
        return;
      }

      const refreshToken =
        capturedUrl?.kind === "legacy-refresh"
          ? capturedUrl.refreshToken
          : params.get("refreshToken");

      if (!refreshToken) {
        setIsProcessing(false);
        return;
      }

      window.history.replaceState({}, "", sanitizeAuthUrl(currentUrl.href));

      try {
        const response = await nhost.auth.refreshToken(
          { refreshToken },
          { signal: AbortSignal.timeout(10_000) }
        );

        if (response.body?.accessToken) {
          // Store the session - response.body is already a valid Session object
          nhost.sessionStorage.set({
            accessToken: response.body.accessToken,
            accessTokenExpiresIn: response.body.accessTokenExpiresIn,
            refreshToken: response.body.refreshToken,
            refreshTokenId: response.body.refreshTokenId,
            user: response.body.user,
          });
          setEstablishedSession(true);
        }
      } catch {
        console.warn("Failed to establish a session from an authentication link");
      } finally {
        setIsProcessing(false);
      }
    };

    handleUrlParams();
  }, []);

  return { isProcessing, establishedSession };
}
